import { inject, Service } from '@angular/core';

import { StorageKeys } from '@core/storage/storage.keys';
import { StorageService } from '@core/storage/storage.service';

/** The server reads at most 32 chars (`POST /auth/otp/verify` `referral_code`, max 32). */
export const REFERRAL_CODE_MAX = 32;
const CODE_RE = /^[A-Za-z0-9_-]{1,32}$/;

/**
 * Pull a referral code off an entry url (docs/KHOYEK-MVP-LAYER-INTEGRATION.md BE11).
 * `?ref=` and `?r=` both carry it («`ref` and `r` are both fine for the link»), and
 * the portal's own invite link (`/invite?code=`) carries `code`. On `/kalma`, `r` is
 * the game's friend-challenge code, never a referral. Anything that is not a plain
 * code (≤ 32 of `A–Z a–z 0–9 _ -`) is ignored.
 */
export function referralCodeFromUrl(pathname: string | null | undefined, search: string | null | undefined): string | null {
  const path = (pathname ?? '').replace(/\/+$/, '').toLowerCase();
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search ?? '');
  } catch {
    return null;
  }
  const isKalma = path === '/kalma' || path.startsWith('/kalma/');
  const isInvite = path === '/invite';
  const candidates = [params.get('ref'), isKalma ? null : params.get('r'), isInvite ? params.get('code') : null];
  for (const c of candidates) {
    const code = (c ?? '').trim();
    if (CODE_RE.test(code)) return code;
  }
  return null;
}

/**
 * ReferralCodeService — keeps an invite code from the entry link until sign-up
 * (BE11). The code is sent ONCE with `POST /api/v3/auth/otp/verify` as
 * `referral_code`; the server applies it to a NEW account only (another user's
 * `owned_code`, case-insensitive) and otherwise ignores it — never an error. After
 * any successful verify the kept code is spent, so it is cleared (the response's
 * `referred_by` says whether it was applied).
 *
 * Device-level, like `DEVICE_ID`: the code exists before any account, so it is not
 * in the user-scoped purge list. SSR: no-op.
 */
@Service()
export class ReferralCodeService {
  private readonly storage = inject(StorageService);

  /** Remember the code carried by the entry url, if any (a newer link wins). */
  captureFromUrl(pathname: string | null | undefined, search: string | null | undefined): void {
    const code = referralCodeFromUrl(pathname, search);
    if (code) this.storage.set(StorageKeys.REFERRAL_CODE, code);
  }

  /** The kept code, or null. */
  pending(): string | null {
    const v = this.storage.get<string>(StorageKeys.REFERRAL_CODE);
    return typeof v === 'string' && CODE_RE.test(v) ? v : null;
  }

  /** Forget the code (spent by a successful verify). */
  clear(): void {
    this.storage.remove(StorageKeys.REFERRAL_CODE);
  }
}
